<template>
  <div class="management-content">
    <div class="title-box">
      <div class="page-title">{{ $t("Header_User") }}</div>
      <div class="right-box">
        <!-- 필터 -->
        <a-popover class="search-filter" placement="bottomRight" trigger="click" v-model:visible="filterState.visible">
          <template #content>
            <div>
              <div class="filter-content">
                <div v-for="item in filterItems" :key="item.key" class="filter-item">
                  <div class="filter-title">{{ $t(item.label) }}</div>
                  <a-checkbox-group v-model:value="filterState[item.model]" :options="item.options"
                    class="check-group" />
                </div>
              </div>
              <hr />
              <div class="filter-button-box">
                <div>
                  <a-button class="filter-button reset" size="small" @click="onFilterReset">
                    {{ $t('Common_FilterReset') }}
                  </a-button>
                </div>
                <div>
                  <a-button class="filter-button confirm" size="small" @click="onFilterConfirm">
                    {{ $t('CH_password1') }}
                  </a-button>
                  <a-button class="filter-button cancel" size="small" @click="onFilterCancel">
                    {{ $t('Common_Cancel') }}
                  </a-button>
                </div>
              </div>
            </div>
          </template>
          <a-button>
            {{ $t('Common_Filter') }}
            <div>
              <span v-if="filterCount" class="filter-count">{{ filterCount }}</span>
              <img v-else src="/img/common/down-arrow.png" />
            </div>
          </a-button>
        </a-popover>
        <!-- 검색 -->
        <a-input-search class="search-input" v-model:value="searchText" :placeholder="$t('Common_Search')"
          @search="onSearch" />
        <div class="page-buttons">
          <a-button class="menu-button" @click="toggleModal">{{ $t("Common_Enroll") }}</a-button>
          <UserAddEditModal v-if="userVisible" mode="add" :userInfo="{}" :visible="userVisible"
            :toggleModal="toggleModal" />
        </div>
      </div>
    </div>
    <CommonTable :loading="loading" :data="tableData" :columns="columns" settingType="user" :fetchData="getUser"
      :pagination="pagination" :handleTableChange="handleTableChange" />
  </div>
</template>

<script>
import {
  computed,
  onUnmounted,
  onMounted,
  onUpdated,
  reactive,
  ref,
  toRefs,
  watch,
} from "vue";
import { useStore } from "vuex";
import axios from "axios";
import { apiUrl } from "@/utils/common";
import CommonTable from "@/components/commonComponents/CommonTable";
import UserAddEditModal from "@/components/sideComponents/UserAddEditModal";
import { useI18n } from "vue-i18n";

export default {
  name: "User",
  components: {
    CommonTable,
    UserAddEditModal,
  },
  setup() {
    const store = useStore();
    const userState = computed(() => store.getters["user/STATE"]);
    const { t } = useI18n();

    const state = reactive({
      tableData: [],
      loading: false,
      userVisible: false,
      searchText: '',
      pagination: {},
      pagesize: 12,
      currentPage: 1,
    });

    const columns = [
      {
        align: "center",
        width: 60,
        slots: { title: "settingTitle", customRender: "settingIcon" },
      },
      {
        title: "No",
        dataIndex: "userNo",
        align: "center",
        width: 80,
      },
      {
        title: t("TP_Edit_2_Reservation_list1"),
        dataIndex: "userName",
        align: "center",
        width: 150,
        ellipsis: true,
      },
      {
        title: "ID",
        dataIndex: "userId",
        align: "center",
        width: 150,
        ellipsis: true,
      },
      {
        title: t("TP_Edit_2_Reservation_list3"),
        dataIndex: "userEmail",
        align: "center",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("User1"),
        dataIndex: "userType",
        align: "center",
        width: 80,
        slots: { customRender: "userType" },
      },
      {
        title: t("User2"),
        dataIndex: "regEpoch",
        align: "center",
        width: 100,
        slots: { customRender: "dateTime" },
      },
    ];

    onMounted(() => {
      getUser();
    });

    watch(
      () => userState.value.needReload,
      (current, prev) => {
        // 사용자 등록/수정/삭제 후 테이블 목록 재조회
        if (current) getUser();
      }
    );

    watch(
      () => state.currentPage,
      () => {
        getUser();
      }
    );

    const toggleModal = () => {
      state.userVisible = !state.userVisible;
    };

    const USER_ROLE_OPTIONS = [
      { label: t("User_Add6"), value: 1000 },
      { label: t("User_Add5"), value: 100 },
      { label: t("User_Add4"), value: 1 },
    ];

    const filterState = reactive({
      visible: false,
      selectedRoles: [],
    });

    const filterItems = [
      {
        key: "userType",
        label: "User1",
        model: "selectedRoles",
        options: USER_ROLE_OPTIONS,
      },
    ];

    // 페이지를 떠날 때 필터 상태 초기화
    onUnmounted(() => {
      store.dispatch('user/CHANGE_FILTER', {});
    });

    // 실제 store에 반영된 필터 개수
    const filterCount = computed(() => {
      const filter = userState.value.param?.filter || {};
      let count = 0;
      Object.entries(filter).forEach(([key, value]) => {
        if (key === 'searchAll') return;
        if (Array.isArray(value)) {
          count += value.length;
        } else if (value) {
          count += 1;
        }
      });
      return count;
    });

    const onFilterConfirm = () => {
      filterState.visible = false;
      store.dispatch('user/CHANGE_FILTER', {
        ...userState.value.param.filter,
        userType: filterState.selectedRoles,
      });
      getUser();
    };

    const onFilterCancel = () => {
      filterState.visible = false;
      filterState.selectedRoles = userState.value.param.filter?.userType || [];
    };

    const onFilterReset = () => {
      filterState.selectedRoles = [];
      filterState.visible = false;
      store.dispatch('user/CHANGE_FILTER', {
        ...userState.value.param.filter,
        userType: [],
      });
      getUser();
    };

    const onSearch = (value) => {
      // 1페이지로 초기화
      state.currentPage = 1;
      getUser();
    };

    const getUser = async () => {
      state.loading = true;
      try {
        // filter 동적 생성
        const filter = {};
        const filterKeys = [
          { key: 'userType', prop: 'userType' },
        ];
        const paramFilter = userState.value.param?.filter || {};
        filterKeys.forEach(({ key, prop }) => {
          const arr = paramFilter[prop];
          if (Array.isArray(arr) && arr.length > 0) {
            filter[key] = arr;
          }
        });
        if (state.searchText) filter.searchAll = state.searchText;
        const params = {
          filter,
          sort: {},
          offset: state.pagesize * (state.currentPage - 1),
          limit: state.pagesize,
        };

        const response = await axios.get(apiUrl + "User", { params });
        if (response && !response.data.error) {
          store.dispatch("user/GET_USER", response);
          state.tableData = response.data.list;
          state.pagination.current = state.currentPage;
          state.pagination.pageSize = state.pagesize;
          state.pagination.total = userState.value.totalCount;
        }
      } catch (error) {
        console.log("get user error", error);
      }
      state.loading = false;
    };

    // 페이지 변경 이벤트
    const handleTableChange = (pagination, filters, sorter) => {
      state.currentPage = pagination.current;
    };

    return {
      ...toRefs(state),
      columns,
      handleTableChange,
      toggleModal,
      USER_ROLE_OPTIONS,
      filterState,
      filterCount,
      filterItems,
      onFilterConfirm,
      onFilterCancel,
      onFilterReset,
      onSearch,
    };
  },
};
</script>

<style></style>